import { Outlet } from 'react-router-dom';
import { Card, ThemeToggle } from '@repletoryreach/sdk-ui';
import { Select } from '@repletoryreach/sdk-ui/inputs';
import { useI18n } from '../../i18n/I18nProvider';
import { LOCALES, type Locale } from '../../i18n/catalog';
import { localeCoverage } from '../../i18n/coverage';
import { BrandLogo } from '../../components/BrandLogo';
import { BrandWordmark } from '../../components/BrandWordmark';

/**
 * The wordmark is a proper noun and is never translated. Split in two so the
 * second half can take the accent colour, as the design reference shows.
 */

/**
 * Split-screen auth shell: brand panel + a centred form card.
 *
 * Implemented from `_UI_improvements/tenant/_auth-layout.md`:
 * - locale and theme controls now exist **before** sign-in (§4 items 1–2). A
 *   user whose console is Afrikaans previously got an English login screen
 *   every time, and a new signup could not pick a language at all.
 * - the brand `<h1>` became a decorative `<div>` so each child page owns the
 *   page's only `<h1>` (§4 item 4): heading order was h1 → h3, with the h1
 *   hidden entirely below the breakpoint.
 * - a condensed brand strip renders below the breakpoint instead of hiding the
 *   panel outright, so the product's only positioning copy still reaches phone
 *   users (§5 larger changes).
 * - footer links give someone who cannot get in an escape hatch (§4 item 3).
 */
export default function AuthLayout() {
  const { locale, setLocale, t } = useI18n();

  const localeSelect = (
    <Select
      size="xs"
      className="rr-auth__control"
      aria-label={t('shell.localePicker')}
      value={locale}
      onChange={(e) => setLocale(e.currentTarget.value as Locale)}
      data={LOCALES.map((l) => {
        const pct = localeCoverage(l.code);
        return {
          value: l.code,
          label: pct === 100 ? l.label : t('shell.locale.partial', { label: l.label, percent: pct })
        };
      })}
    />
  );

  return (
    <div className="rr-auth">
      <div className="rr-auth__brand">
        <div className="rr-auth__brand-top">
          <div className="rr-auth__brand-lockup">
            <BrandLogo size={50} className="rr-auth__brand-mark" />
            <BrandWordmark className="rr-auth__brand-name" />
          </div>
          <span className="rr-auth__brand-rule" aria-hidden />

          <h2 className="rr-auth__brand-headline">{t('auth.layout.headline')}</h2>
          <p className="rr-auth__brand-tagline">{t('auth.layout.tagline')}</p>

          {/* The panel used to hold a wordmark and one line of copy on an
              otherwise empty half of the screen. It is the first thing a
              prospect sees, so it now carries a short, accurate brief of what
              the product actually does. Every claim maps to a shipped
              capability in _docs/00-executive-summary.md. */}
          <ul className="rr-auth__features">
            <Feature icon={<InboxIcon />} title={t('auth.layout.feature.inbox.title')} body={t('auth.layout.feature.inbox.body')} />
            <Feature icon={<PanelSendIcon />} title={t('auth.layout.feature.broadcasts.title')} body={t('auth.layout.feature.broadcasts.body')} />
            <Feature icon={<SparkIcon />} title={t('auth.layout.feature.automation.title')} body={t('auth.layout.feature.automation.body')} />
            <Feature icon={<WalletIcon />} title={t('auth.layout.feature.billing.title')} body={t('auth.layout.feature.billing.body')} />
          </ul>

          <p className="rr-auth__brand-trust">
            <PanelShieldIcon /> {t('auth.layout.trust')}
          </p>
        </div>

        {/* Both controls exist here only; the panel is the one surface every
            auth page shares. */}
        <div className="rr-auth__brand-controls">
          {localeSelect}
          <ThemeToggle size="xs" />
        </div>
      </div>

      <div className="rr-auth__panel">
        {/* Condensed brand for ≤800px, where the full panel is not rendered. */}
        <div className="rr-auth__mobile-brand">
          <BrandLogo size={50} className="rr-auth__brand-mark" />
          <BrandWordmark className="rr-auth__brand-name" />
        </div>

        <Card withBorder className="rr-auth__card">
          <Outlet />
        </Card>

        <footer className="rr-auth__footer">
          <nav className="rr-auth__footer-links" aria-label={t('auth.layout.support')}>
            <a href="https://docs.repletoryreach.com" target="_blank" rel="noreferrer noopener">
              <BookIcon /> {t('auth.layout.docs')}
            </a>
            <span className="rr-auth__footer-sep" aria-hidden />
            <a href="https://repletoryreach.com/status" target="_blank" rel="noreferrer noopener">
              <ShieldIcon /> {t('auth.layout.status')}
            </a>
            <span className="rr-auth__footer-sep" aria-hidden />
            <a href="mailto:support@repletoryreach.com">
              <HeadsetIcon /> {t('auth.layout.support')}
            </a>
          </nav>
          <p className="rr-auth__copyright">
            {t('auth.layout.copyright', { year: new Date().getFullYear() })}
          </p>
        </footer>

        {/* Controls follow the card once the brand panel is gone. */}
        <div className="rr-auth__mobile-controls">
          {localeSelect}
          <ThemeToggle size="xs" />
        </div>
      </div>
    </div>
  );
}

function Feature({ icon, title, body }: { icon: React.ReactNode; title: string; body: string }) {
  return (
    <li className="rr-auth__feature">
      <span className="rr-auth__feature-icon" aria-hidden>{icon}</span>
      <span className="rr-auth__feature-text">
        <strong>{title}</strong>
        <span>{body}</span>
      </span>
    </li>
  );
}

function PanelIcon({ children }: { children: React.ReactNode }) {
  return (
    <svg width="17" height="17" viewBox="0 0 20 20" fill="none" stroke="currentColor"
      strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      {children}
    </svg>
  );
}
function InboxIcon() { return <PanelIcon><path d="M2.5 11.5h4l1.2 2h4.6l1.2-2h4" /><path d="M4.2 4.5h11.6l1.7 7v4.5a1 1 0 01-1 1H3.5a1 1 0 01-1-1V11.5z" /></PanelIcon>; }
function PanelSendIcon() { return <PanelIcon><path d="M17.5 2.5L9 11" /><path d="M17.5 2.5l-5.4 15-3.1-6.4L2.5 8z" /></PanelIcon>; }
function SparkIcon() { return <PanelIcon><path d="M10 2.5v3.2M10 14.3v3.2M2.5 10h3.2M14.3 10h3.2M4.7 4.7l2.2 2.2M13.1 13.1l2.2 2.2M4.7 15.3l2.2-2.2M13.1 6.9l2.2-2.2" /><circle cx="10" cy="10" r="2.4" /></PanelIcon>; }
function WalletIcon() { return <PanelIcon><rect x="2.5" y="5" width="15" height="10.5" rx="2" /><path d="M2.5 8.5h15" /><circle cx="14.2" cy="12" r="0.9" fill="currentColor" /></PanelIcon>; }
function PanelShieldIcon() { return <PanelIcon><path d="M10 2l6.5 2.8v4.4c0 4.8-6.5 8.3-6.5 8.3S3.5 14 3.5 9.2V4.8L10 2z" /><path d="M7.4 9.8l1.9 1.9 3.4-3.6" /></PanelIcon>; }


function FooterIcon({ children }: { children: React.ReactNode }) {
  return (
    <svg width="15" height="15" viewBox="0 0 16 16" fill="none" stroke="currentColor"
      strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      {children}
    </svg>
  );
}
function BookIcon() { return <FooterIcon><path d="M2 3.5h4.5A1.5 1.5 0 018 5v8a1.5 1.5 0 00-1.5-1.5H2z" /><path d="M14 3.5H9.5A1.5 1.5 0 008 5v8a1.5 1.5 0 011.5-1.5H14z" /></FooterIcon>; }
function ShieldIcon() { return <FooterIcon><path d="M8 1.5l5.5 2.4v3.6c0 4-5.5 7-5.5 7s-5.5-3-5.5-7V3.9L8 1.5z" /><path d="M5.8 8l1.6 1.6L10.4 6.6" /></FooterIcon>; }
function HeadsetIcon() { return <FooterIcon><path d="M3 9V8a5 5 0 0110 0v1" /><path d="M3 9h1.6a1 1 0 011 1v2a1 1 0 01-1 1H3zM13 9h-1.6a1 1 0 00-1 1v2a1 1 0 001 1H13z" /></FooterIcon>; }
